import { useTranslate } from '@va/localization';
import { TooltipOptions } from '@va/types/tooltip';
import {
  Button,
  fontWeights,
  GrayGradientDivider,
  Paragraph,
  paragraphSizes,
  ParagraphWithTooltip,
} from '@va/ui/design-system';
import { TooltipWrapper } from '@va/ui/tooltips';
import { DropdownArrow } from '@va/util/components';
import { useCheckOverflow, useOutsideClick } from '@va/util/hooks';
import classNames from 'classnames';
import { isNil } from 'lodash';
import { PropsWithChildren, ReactNode, useCallback, useMemo, useRef, useState } from 'react';

export type AccordionSectionProps = {
  bgColorClass?: string;
  icon?: React.ReactNode;
  title?: string;
  titleTooltip?: string | React.ReactNode;
  titleTooltipProps?: TooltipOptions;
  subtitle?: string;
  isOpenExternal?: boolean;
  toggleIsOpenExternal?: () => void;
  heightClass?: string;
  className?: string;
  wrapperClassName?: string;
  titleClassName?: string;
  subtitleClassName?: string;
  arrowButtonClassName?: string;
  isInitiallyOpen?: boolean;
  infoIcon?: React.ReactElement;
  required?: boolean;
  'data-testid'?: string;
  optional?: boolean;
  disabled?: boolean;
  disabledTooltip?: ReactNode;
  floating?: boolean;
  shouldCloseOnOutsideClick?: boolean;
  size?: 'small' | 'large';
};

export const AccordionSection: React.FC<PropsWithChildren<AccordionSectionProps>> = ({
  children,
  bgColorClass = 'bg-white-snow',
  icon,
  title,
  subtitle,
  titleTooltip,
  titleTooltipProps,
  isOpenExternal,
  toggleIsOpenExternal,
  size = 'large',
  heightClass = size === 'small' ? 'h-12' : 'h-72',
  className,
  wrapperClassName,
  titleClassName,
  subtitleClassName,
  arrowButtonClassName,
  isInitiallyOpen = false,
  infoIcon,
  required = false,
  'data-testid': dataTestId,
  optional = false,
  disabled = false,
  disabledTooltip,
  floating = false,
  shouldCloseOnOutsideClick = false,
}) => {
  const translate = useTranslate();
  const titleRef = useRef(null);
  const accordionRef = useRef(null);
  const isTitleOverflowing = useCheckOverflow(titleRef);
  const [isOpenInternal, setIsOpenInternal] = useState(isInitiallyOpen);

  const isOpen = !isNil(isOpenExternal) ? isOpenExternal : isOpenInternal;

  const getTitleTooltip = useCallback(() => {
    if (titleTooltip) return titleTooltip;
    if (isTitleOverflowing) return title;
    return undefined;
  }, [isTitleOverflowing, title, titleTooltip]);

  const showDisabledTooltip = useMemo(() => {
    return disabledTooltip && disabled;
  }, [disabled, disabledTooltip]);

  function toggleOpen() {
    if (disabled) return;
    if (!isNil(isOpenExternal) && toggleIsOpenExternal) {
      toggleIsOpenExternal();
    } else {
      setIsOpenInternal((prev) => !prev);
    }
  }

  useOutsideClick(
    accordionRef,
    () => {
      if (isOpen) toggleOpen();
    },
    shouldCloseOnOutsideClick,
  );

  return (
    <TooltipWrapper content={disabledTooltip} disabled={!showDisabledTooltip} ref={accordionRef}>
      <div
        className={classNames(
          size === 'small' ? 'rounded-15' : 'rounded-18',
          {
            '!bg-primary-lighter-06 opacity-80 cursor-not-allowed': disabled,
            relative: floating,
          },
          bgColorClass,
          wrapperClassName,
        )}
      >
        <div
          onClick={toggleOpen}
          className={classNames(
            'flex items-center overflow-hidden px-3 text-gray-dark',
            size === 'small' ? 'gap-2 rounded-15' : 'gap-3 rounded-18',
            {
              'hover:bg-gray-concrete hover:transition-colors duration-300': !disabled,
              'text-gray-devil': disabled,
              '!rounded-b-none': isOpen && children,
            },
            heightClass,
            className,
          )}
          data-testid={dataTestId}
        >
          {icon && <div className='shrink-0'>{icon}</div>}
          <div className={classNames('overflow-hidden w-full', { 'ml-2': size !== 'small' })}>
            {title && (
              <TooltipWrapper
                {...titleTooltipProps}
                content={getTitleTooltip()}
                disabled={!titleTooltip && !isTitleOverflowing}
              >
                <Paragraph
                  ref={titleRef}
                  className={classNames(
                    'truncate',
                    {
                      'underline-dashed': titleTooltip,
                    },
                    titleClassName,
                  )}
                  colorClassName='text-gray-dark-charcoal'
                  weight={fontWeights.medium}
                >
                  <>
                    {title}
                    {optional && <span className='text-gray-dusty ml-1'>({translate('labels.optional')})</span>}
                    {required && <span className='text-red-negative ml-1'>*</span>}
                  </>
                </Paragraph>
              </TooltipWrapper>
            )}

            {subtitle && (
              <ParagraphWithTooltip
                size={paragraphSizes.tiny}
                colorClassName='text-gray-dark-charcoal'
                weight={fontWeights.medium}
                className={subtitleClassName}
              >
                {subtitle}
              </ParagraphWithTooltip>
            )}
          </div>
          {infoIcon && <div>{infoIcon}</div>}
          <Button
            disabled={disabled}
            className={classNames(
              'ml-auto',
              size === 'small' ? '!bg-transparent' : '!bg-gray-concrete',
              arrowButtonClassName,
            )}
            color='tertiary'
            icon={() => <DropdownArrow color='#696969' open={isOpen} />}
          />
        </div>
        {isOpen && children && (
          <div className='overflow-hidden transition-all duration-200'>
            {!floating && <GrayGradientDivider />}
            {children}
          </div>
        )}
      </div>
    </TooltipWrapper>
  );
};
